/* hs-eslint ignored failing-rules */

/* eslint-disable no-use-before-define */

/* eslint-disable hubspot-dev/no-confusing-browser-globals */
'use es6';

import { result } from '../util/miniUnderscore.js';
/**
 * Model implementing the observer pattern.
 * @typedef {Object} Observable
 * @property {*}        *     - Properties added via constructor.
 * @property {Function} on    - Subscribe.
 * @property {Function} off   - Unsubscribe.
 */

/**
 * Object param used to add a field to the model.
 * @typedef {Object} ObservableField
 * @property {String}                     name      - Name of property. Required.
 * @property {*}                          value     - Value assigned on instantiation. Optional.
 * @property {ObservableField~validator}  validator - Validation function used in setter. Optional.
 */

/**
 * Validates new value for field.  Return `true` if valid. Otherwise return `false`
 * or throw a `TypeError`.
 * @callback ObservableField~validator
 * @param {*} value - New value to be assigned to field.
 */

const privy = new WeakMap();
const privyFields = new WeakMap();
const privyHandlers = new WeakMap();
const EMPTY_ARRAY = Object.freeze([]);

function throwFnTypeError(method, fn) {
  throw new TypeError(`Observable#${method} expected function, received type "${typeof fn}"`);
}

function checkForObservable(instance) {
  if (instance instanceof Observable) {
    return;
  }

  throw new TypeError(`Expected Observable instance, received "${instance}"`);
}

const noopValidate = () => true;
/**
 * Adds a field to the model.
 *
 * @param {ObservableField} observableField
 */


function addField({
  name: field,
  value: initValue,
  validator
}) {
  privyFields.get(this)[field] = initValue;
  Object.defineProperty(this, field, {
    enumerable: true,

    get() {
      return privyFields.get(this)[field];
    },

    set(value) {
      const p = privyFields.get(this);

      if (p[field] === value) {
        return;
      }

      if (!validator(value)) {
        return;
      }

      const oldValue = p[field];
      p[field] = value;
      privyHandlers.get(this).forEach(({
        fn,
        context
      }) => {
        fn.call(context, {
          field,
          value,
          oldValue,
          model: this
        });
      });
    }

  });
}
/**
 * @param {String|Object|ObservableField} field
 * @returns {ObservableField}
 */


function coerceToObservableField(field) {
  if (field && typeof field === 'string') {
    return Object.freeze({
      name: field,
      value: undefined,
      validator: noopValidate
    });
  }

  if (field && field.name && typeof field.name === 'string') {
    const {
      name,
      value,
      validator = noopValidate
    } = field;
    return Object.freeze({
      name,
      value,
      validator
    });
  }

  throw new TypeError(`Invalid ObservableField, received \`${name}\``);
}
/**
 * @class Observable
 * @abstract
 */


export default class Observable {
  /**
   * @constructor
   * @return {Observable}
   */
  constructor() {
    const defaultFields = new Map();
    privy.set(this, {
      defaultFields
    });
    privyFields.set(this, {});
    privyHandlers.set(this, new Map());
    const DEFAULT_FIELDS = result(this.constructor, 'DEFAULT_FIELDS');

    if (DEFAULT_FIELDS) {
      DEFAULT_FIELDS.forEach(field => {
        const observableField = coerceToObservableField(field);
        const {
          name
        } = observableField;
        addField.call(this, observableField);
        defaultFields.set(name, observableField);
      });
    }

    Object.preventExtensions(this);
  }
  /**
   * Subscribe to model.
   *
   * @param  {Function} fn
   * @param  {Object}   context - optional
   * @return {Undefined}
   * @throws {TypeError} If param `fn` is not a function.
   */


  on(fn, context) {
    if (typeof fn === 'function') {
      privyHandlers.get(this).set(fn, {
        fn,
        context
      });
    } else {
      throwFnTypeError('on', fn);
    }
  }
  /**
   * Unsubscribe to model.
   *
   * @param  {Function} fn  - Remove listener `fn`.
   *                        If `fn` is null/undefined then all all listeners will be removed.
   * @return {Undefined}
   * @throws {TypeError} If param `fn` is not a function or `null`/`undefined`.
   */


  off(fn) {
    if (fn == null) {
      privyHandlers.get(this).clear();
    } else if (typeof fn === 'function') {
      privyHandlers.get(this).delete(fn);
    } else {
      throwFnTypeError('off', fn);
    }
  }

}
/**
 * Defines the model's fields.
 * Array or function returning an array of default fields to
 * be used by the constructor.
 *
 * @virtual
 * @type {Array.<String|ObservableField>|Function}
 */

Observable.DEFAULT_FIELDS = EMPTY_ARRAY;
/**
 * Resets all fields to initial defaults.
 *
 * @param {Observable} model
 * @return {Undefined}
 * @throws {TypeError}
 */

Observable.reset = function reset(model) {
  checkForObservable(model);
  const {
    defaultFields
  } = privy.get(model);
  defaultFields.forEach(({
    name,
    value
  }) => {
    model[name] = value;
  });
};
/**
 * True if all model fields match initial default fields, false otherwise.
 *
 * @param {Observable} model
 * @returns {Boolean}
 * @throws {TypeError}
 */


Observable.isDefaultFields = function isDefaultFields(model) {
  checkForObservable(model);
  let isDefaults = true;
  const {
    defaultFields
  } = privy.get(model);

  for (const [, {
    name,
    value
  }] of defaultFields) {
    if (value !== model[name]) {
      isDefaults = false;
      break;
    }
  }

  return isDefaults;
};